
import { defineComponent, ref, computed, onMounted } from 'vue';

interface SecurityAudit {
  id: string;
  title: string;
  client: string;
  project: string;
  date: string;
  description: string;
  categories: string[];
  group?: string;
  blogUrl: string;
  pdfUrl: string;
  logoUrl?: string;
}

interface AuditsData {
  audits: SecurityAudit[];
}

export default defineComponent({
  name: 'LocalSecurityAudits',
  props: {
    apiUrl: {
      type: String,
      default: '/data/security-audits.json'
    },
    branding: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    // Reactive data
    const loading = ref(true);
    const error = ref<string | null>(null);
    const audits = ref<SecurityAudit[]>([]);
    const showAll = ref(false);
    const initialDisplayCount = 3;

    // Computed properties
    const showBranding = computed(() => props.branding);

    const displayedAudits = computed(() => {
      // Data is already sorted in reverse chronological order (newest first)
      if (showAll.value) {
        return audits.value;
      }
      // Show only the most recent audits when collapsed
      return audits.value.slice(0, initialDisplayCount);
    });

    const formatDate = (dateString: string): string => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      });
    };

    const getGroupClass = (group: string): string => {
      if (group === 'infra-protocols') return 'audit-group-infra';
      if (group === 'contracts-defi') return 'audit-group-contracts';
      return 'audit-group-other';
    };

    const toggleShowAll = () => {
      showAll.value = !showAll.value;
    };

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;

        const response = await fetch(props.apiUrl);
        if (!response.ok) {
          throw new Error(`Failed to load audits data: ${response.status}`);
        }

        const data: AuditsData = await response.json();
        audits.value = data.audits;

      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load audits data';
        console.error('Security Audits Data Error:', err);
      } finally {
        loading.value = false;
      }
    };

    // Lifecycle
    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      error,
      audits,
      showAll,
      initialDisplayCount,
      displayedAudits,
      showBranding,
      formatDate,
      getGroupClass,
      toggleShowAll
    };
  }
});
